import { useEffect } from 'react';

/**
 * Adds the `.in` class to any element with the `.reveal` class as it crosses
 * 10% from the bottom of the viewport. Pairs with the `.reveal` / `.reveal.in`
 * transition in design-system.css.
 *
 * Call once at the App root. An IntersectionObserver picks up the initial set
 * of `.reveal` elements on mount; a paired MutationObserver picks up any
 * `.reveal` element that mounts later — required because React Router lazy
 * routes (Suspense) resolve AFTER this hook's effect runs, so without the
 * MutationObserver the late-mounted elements would never be observed and
 * would stay frozen at `opacity: 0`.
 *
 * The IO is re-created on every `dependencyKey` change so language switches
 * (which can re-render copy sections) get their reveals re-observed.
 */
export function useReveal(dependencyKey?: unknown): void {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    const observeIfReveal = (node: Element) => {
      if (
        node instanceof HTMLElement &&
        node.classList.contains('reveal') &&
        !node.classList.contains('in')
      ) {
        io.observe(node);
      }
    };

    // Initial scan — catches everything already in the DOM at hook-fire time.
    document
      .querySelectorAll<HTMLElement>('.reveal:not(.in)')
      .forEach((el) => io.observe(el));

    // Late-mount scan — catches `.reveal` elements added after the initial
    // effect (Suspense-resolved lazy routes, conditional renders, etc.).
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node instanceof Element) {
            observeIfReveal(node);
            node.querySelectorAll('.reveal:not(.in)').forEach(observeIfReveal);
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [dependencyKey]);
}
